import React, { useState } from "react";
import { Card, CardContent } from "../../ui/card";
import { Headline, Kpi, PainBanner, Pill, SectionTitle } from "./PersonaUi";
import { CRWD_HOLDERS, EVIDENCE, HF_BOOK } from "./personaData";

const risk = (r) => (r.crowd >= 90 || r.exits >= 2 ? "HIGH" : r.crowd >= 75 ? "ELEVATED" : "NORMAL");
const RT = { HIGH: "bad", ELEVATED: "alert", NORMAL: "muted" };

export default function HfCrowding() {
  const [sel, setSel] = useState("CRWD");
  const book = [...HF_BOOK].sort((a, b) => b.crowd - a.crowd);
  const hi = book.filter((r) => risk(r) === "HIGH");
  const hiW = hi.reduce((s, r) => s + r.w, 0);
  const lock = hi.reduce((s, r) => s + r.w * r.lock, 0) / (hiW || 1);
  const row = book.find((r) => r.sym === sel);
  const hasDrill = sel === "CRWD";
  return (
    <div className="space-y-4" data-testid="persona-hf-crowding">
      <PainBanner testid="persona-hf-crowding-pain" persona="HF ANALYST · CROWDING & EXIT RISK"
        pain="Am I in a crowded trade — who else owns it, and who is the marginal seller if it unwinds?"
        answer="book ranked by crowding, holder-level drill-down" />
      <Headline testid="hf-crowding-headline">{hi.length} positions ({hiW.toFixed(1)}% of gross) sit in the top crowding decile</Headline>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="hf-crowd-kpi-high" label="High-risk positions" value={hi.length} tone="text-red-400" sub={hi.map((r) => r.sym).join(", ")} />
        <Kpi testid="hf-crowd-kpi-lockstep" label="Lockstep holder share" value={`${Math.round(lock * 100)}%`} sub="weighted avg, high-risk names" />
        <Kpi testid="hf-crowd-kpi-exits" label="Skilled exits in book" value={HF_BOOK.reduce((s, r) => s + r.exits, 0)} tone="text-amber-400" sub="last 2 quarters" />
        <Kpi testid="hf-crowd-kpi-evidence" label="Top-decile penalty" value="−2.3%" sub="3M vs SPY, t −3.49" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <Card className="xl:col-span-2"><CardContent className="pt-4">
          <SectionTitle>Your book — crowding percentile</SectionTitle>
          <div className="space-y-1.5" data-testid="hf-crowding-ladder">
            {book.map((r) => (
              <button key={r.sym} onClick={() => setSel(r.sym)} data-testid={`hf-crowding-row-${r.sym}`}
                className={`w-full text-left rounded px-2.5 py-2 border transition-colors ${sel === r.sym ? "border-sky-500/60 bg-sky-500/5" : "border-transparent hover:border-border"}`}>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-mono font-bold w-12">{r.sym}</span>
                  <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden mx-2">
                    <div className={`h-full ${r.crowd >= 90 ? "bg-red-500" : r.crowd >= 75 ? "bg-amber-500" : "bg-zinc-500"}`} style={{ width: `${r.crowd}%` }} />
                  </div>
                  <span className="font-mono w-10 text-right">{r.crowd}th</span>
                  <Pill tone={RT[risk(r)]}>{risk(r)}</Pill>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 pl-12">{r.w.toFixed(1)}% gross · {r.hold} holders · {r.exits} skilled exits · {r.regime}</div>
              </button>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground mt-2">{EVIDENCE.crowd}</div>
        </CardContent></Card>
        <Card className="xl:col-span-3"><CardContent className="pt-4">
          <SectionTitle right={<Pill tone={RT[risk(row)]}>{sel} · {row.crowd}th pct · {risk(row)}</Pill>}>Who owns {sel} — and who sells first</SectionTitle>
          {!hasDrill && (
            <div className="rounded border border-border/60 bg-muted/10 p-6 text-center text-[13px] text-muted-foreground" data-testid="hf-crowding-sample-note">
              In the product every position gets this holder drill-down. The mockup sample is wired for CRWD only.
              <div><button className="mt-3 text-sky-300 underline" onClick={() => setSel("CRWD")} data-testid="hf-crowding-show-crwd">Show the CRWD sample</button></div>
            </div>
          )}
          {hasDrill && (<>
          <table className="w-full text-xs" data-testid="hf-crowding-holders-table">
            <thead className="border-b border-border text-muted-foreground">
              <tr>{["Holder", "Skill pct", "Last action", "% of their book", "Also crowded in", "Days to exit (20% ADV)"].map((h, i) => (
                <th key={h} className={`py-1.5 px-2 ${i === 0 || i === 4 ? "text-left" : "text-right"}`}>{h}</th>))}</tr>
            </thead>
            <tbody>
              {CRWD_HOLDERS.map((h) => (
                <tr key={h.fund} className="border-b border-border/40" data-testid="hf-crowding-holder-row">
                  <td className="py-2 px-2 font-medium">{h.fund}</td>
                  <td className={`py-2 px-2 text-right font-mono ${h.skill >= 80 ? "text-emerald-400" : ""}`}>{h.skill}th</td>
                  <td className={`py-2 px-2 text-right font-mono ${h.act.startsWith("TRIM") || h.act === "EXIT" ? "text-red-400" : h.act.startsWith("ADD") ? "text-emerald-400" : "text-muted-foreground"}`}>{h.act}</td>
                  <td className="py-2 px-2 text-right font-mono">{h.book}</td>
                  <td className="py-2 px-2 text-[11px] text-muted-foreground">{h.overlap}</td>
                  <td className="py-2 px-2 text-right font-mono">{h.days ? h.days.toFixed(1) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4" data-testid="hf-crowding-read">
            <div className="rounded border border-red-500/30 bg-red-500/5 p-3 text-[11px]"><b>Marginal sellers:</b> Tiger (−18%) and Jericho (−31%) are already trimming; Whale Rock (81st pct) has exited.</div>
            <div className="rounded border border-border/60 p-3 text-[11px]"><b>Hotel risk:</b> 5 of the 6 largest holders also own NVDA or PANW — a sector unwind would hit all three together.</div>
            <div className="rounded border border-border/60 p-3 text-[11px]"><b>Liquidity:</b> top holders need ~4.8 days at 20% ADV to exit — the queue is long if they move together.</div>
          </div>
          </>)}
          <div className="text-[10px] text-muted-foreground mt-3">Sample data. Holder actions from 13F (up to 45-day lag); days-to-exit is an illustrative liquidity estimate.</div>
        </CardContent></Card>
      </div>
    </div>
  );
}
